import { Button } from "@/components/ui/button";
import { Clock, Euro, Link, File, Train, Bus, Car } from "lucide-react";
import { TransportationActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { DocumentationList } from "@/components/ui/documentation-list";

interface TransportationCardProps {
  activity: TransportationActivity;
  onEdit: (activity: TransportationActivity) => void;
  onDelete: (activity: TransportationActivity) => void;
  onDuplicate?: (activity: TransportationActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
}

export const TransportationCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false
}: TransportationCardProps) => {
  // Helper function to get transport icon
  const getTransportIcon = (transport_type: string) => {
    switch (transport_type) {
      case 'train': return <Train className="h-4 w-4" />;
      case 'bus': return <Bus className="h-4 w-4" />;
      case 'car': return <Car className="h-4 w-4" />;
      case 'taxi': return <Car className="h-4 w-4" />;
      case 'metro': return <Train className="h-4 w-4" />;
      default: return <Car className="h-4 w-4" />;
    }
  };

  const renderAlwaysVisible = () => (
    <>
      {activity.travels && activity.travels.length > 0 && (
        <div className="flex items-center gap-2">
          {getTransportIcon(activity.transport_type)}
          <span>{activity.travels[0].address} → {activity.travels[activity.travels.length - 1].address}</span>
        </div>
      )}
      {activity.travels && activity.travels.length > 0 && activity.travels[0].time && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{activity.travels[0].time}</span>
        </div>
      )}
      {activity.travels && activity.travels.length > 2 && (
        <div className="flex items-center gap-2">
          <span>{activity.travels.length - 2 === 1 ? `1 stop` : `${activity.travels.length - 2} stops`}</span>
        </div>
      )}
      {activity.cost && (
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4" />
          <span>{activity.cost} (only visible to you)</span>
        </div>
      )}
    </>
  );

  const renderCollapsible = () => (
    <>
      {/* Description */}
      {activity.description && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Description</span>
          <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
        </div>
      )}

      {/* Company */}
      {activity.company && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Company</span>
          <p className="text-sm text-gray-600">{activity.company}</p>
        </div>
      )}

      {/* Travel Route */}
      {activity.travels && activity.travels.length > 0 && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Travel Route</span>
          <div className="space-y-2">
            {activity.travels.map((travel, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">
                  <div className="font-medium">
                    {index === 0 ? 'From ' : index === activity.travels.length - 1 ? 'To ' : `Stop ${index} `}
                    <span className="font-normal">{travel.address}</span>
                  </div>
                  <div>{travel.date} at {travel.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documentation */}
      <DocumentationList documentation={activity.documentation || []} />

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          {/* Link Button */}
          {activity.link && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={() => window.open(activity.link, '_blank')}
            >
              <Link className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="truncate max-w-[200px] inline-block">
                {activity.link}
              </span>
            </Button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
